import { ArrowRightLeft, PackagePlus, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { EvidenceIntakeModal } from '../../../evidence-management/evidence/components/EvidenceIntakeModal';
import { EvidenceTransferModal } from '../../../evidence-management/evidence/components/EvidenceTransferModal';
import {
  DESTINATION_LABEL,
  REQUEST_STATUS,
  type EvidenceRequestListItem,
} from '../../../evidence-management/evidence/evidenceTypes';
import { useEmployeePrerequisite } from '../../../case-management/cases/useEmployeePrerequisite';
import { evidenceRequestsApi } from '../evidenceRequestsApi';

/**
 * The requester's own EVIDENCE REQUESTS — product call, 09 Oct 2026: *in the selfservice, create an
 * evidence request for receiving/transfer of evidence*.
 *
 * **IT LISTS ONLY WHAT THIS ACCOUNT RAISED, and nothing here says so to the server.** The scope is
 * fixed by the route — there is no scope parameter on the wire, so nothing this page sends can widen it.
 *
 * **Two things to raise, one menu**: a RECEIVING request (the Receive form's rows) and a TRANSFER
 * request (the Transfer form's destination, items and details) — the same two forms the case's team
 * then completes, so a request cannot ask for what the team's form could not record.
 *
 * **There is no Clear data and no delete** — a request is raised whole and then decided, by the team
 * or by its requester's own Cancel.
 */
export function EvidenceRequestsIndexPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const canCreate = usePermission('evidence-requests.create');

  const [raising, setRaising] = useState<'receiving' | 'transfer' | null>(null);
  const [cancelling, setCancelling] = useState<EvidenceRequestListItem | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    TWO REFUSALS ON THE ACCOUNT, announced before they fire (CLAUDE.md): the caller must be an
    EMPLOYEE, and a MEMBER of at least one case — no key grants either. Both seeded met; a feed that
    fails leaves them met, so a lookup that never answers locks nobody out.
  */
  const employee = useEmployeePrerequisite(
    evidenceRequestsApi.eligibility,
    "Evidence requests are open only to employees, and your account is not linked to an employee's personnel record — so you cannot raise a request or be added to a case. Link it on the personnel record's Account card, or ask HR to.",
  );
  const [hasCase, setHasCase] = useState(true);

  useEffect(() => {
    if (!canCreate) return;
    const controller = new AbortController();

    evidenceRequestsApi
      .cases(controller.signal)
      .then((cases) => {
        if (!controller.signal.aborted) setHasCase(cases.length > 0);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [canCreate, reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      evidenceRequestsApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          kind: query.facets.kind?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: EvidenceRequestListItem): MenuItem[] =>
      row.isActive && row.status === REQUEST_STATUS.pending
        ? [
            {
              key: 'cancel',
              label: 'Cancel request',
              icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
              permission: 'evidence-requests.cancel',
              onClick: () => setCancelling(row),
            },
          ]
        : [],
    [],
  );

  const createMenu: MenuItem[] = [
    {
      key: 'receiving',
      label: 'Receiving request',
      icon: <PackagePlus className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-requests.create',
      onClick: () => setRaising('receiving'),
    },
    {
      key: 'transfer',
      label: 'Transfer request',
      icon: <ArrowRightLeft className="h-4 w-4" aria-hidden="true" />,
      permission: 'evidence-requests.create',
      onClick: () => setRaising('transfer'),
    },
  ];

  async function runCancel() {
    if (!cancelling) return;

    setRunning(true);
    setError(null);

    try {
      await evidenceRequestsApi.cancel(cancelling.id);
      toast.success(`${cancelling.requestNumber} was cancelled.`);
      setCancelling(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The request could not be cancelled.', 0));
    } finally {
      setRunning(false);
    }
  }

  function raised(requestNumber: string) {
    setRaising(null);
    toast.success(`${requestNumber} was sent to the case's team.`);
    refresh();
  }

  return (
    <>
      <IndexPage<EvidenceRequestListItem>
        module="evidence-requests"
        breadcrumb={['Self Service', 'Evidence', 'Evidence Request']}
        columns={COLUMNS}
        facets={[
          { key: 'stage', label: 'Stage' },
          { key: 'kind', label: 'Type' },
          { key: 'status', label: 'Status' },
        ]}
        // Filed by STAGE — the question a requester opens this with is what the team did with each.
        folderNavFacet="stage"
        // BOTH entity types — a register naming only the parent is blind to every write to its lines.
        auditEntityTypes={['EvidenceRequest', 'EvidenceRequestLine']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by request or case number"
        emptyMessage="No evidence requests yet. Raise one to ask a case's team to receive evidence, or to transfer it to a court or to disposal."
        prerequisites={[
          employee,
          {
            label: 'a case you are a member of',
            met: hasCase,
            detail:
              "A request is raised on a case you are a member of, and you are not a member of any case yet. A case's team adds you from the case's Team.",
            to: '/cases',
            permission: 'cases.read',
          },
        ]}
        createMenu={createMenu}
        onRowOpen={(row) => navigate(`/self-service/evidence-requests/${row.id}`)}
        openPermission="evidence-requests.read"
        rowActions={rowActions}
        otherInfo={(row) => row}
      />

      {raising === 'receiving' ? (
        <EvidenceIntakeModal mode="request" onClose={() => setRaising(null)} onRequested={(saved) => raised(saved.requestNumber)} />
      ) : null}

      {raising === 'transfer' ? (
        <EvidenceTransferModal mode="request" onClose={() => setRaising(null)} onRequested={(saved) => raised(saved.requestNumber)} />
      ) : null}

      {cancelling ? (
        <ConfirmDialog
          open
          id="evidence-request-cancel-dialog"
          title={`Cancel ${cancelling.requestNumber}`}
          // A write — it confirms with the tick; no password, cancelling one's own request is an ordinary act.
          requireConfirm
          body={
            <div className="flex flex-col gap-2 text-xs text-slate-600">
              <p>
                The request leaves the case&apos;s Requests and stays here as Cancelled. Nothing was received or
                transferred, and a cancelled request cannot be reopened — raise a new one if it is still needed.
              </p>
              {error ? <p className="text-red-700">{error.message}</p> : null}
            </div>
          }
          loading={running}
          onConfirm={() => void runCancel()}
          onClose={() => {
            setCancelling(null);
            setError(null);
          }}
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<EvidenceRequestListItem>[] = [
  {
    key: 'requestNumber',
    header: 'Request No.',
    width: 160,
    sortable: true,
    render: (row) => <TextCell value={row.requestNumber} mono />,
  },
  {
    /*
      THE CASE — number and title on one line, the case list's own anchor shape. A case the requester can
      no longer open keeps its number and the fixed mask, with the screen-reader sentence saying why.
    */
    key: 'caseNumber',
    header: 'Case',
    width: 360,
    sortable: true,
    summary: true,
    render: (row) =>
      row.isRestricted ? (
        <span className="truncate text-slate-500">
          <span className="font-mono">{row.caseNumber}</span>
          {' — '}
          <span aria-hidden="true" className="font-mono text-slate-400">
            {row.caseTitle}
          </span>
          <span className="sr-only">Title withheld — you can no longer open this case</span>
        </span>
      ) : (
        <TextCell code={row.caseNumber} value={row.caseTitle} />
      ),
  },
  {
    key: 'kind',
    header: 'Type',
    width: 120,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.kindLabel} />,
  },
  {
    // A receiving request goes nowhere — the em dash says the field does not apply.
    key: 'destination',
    header: 'Transfer to',
    width: 130,
    render: (row) => <TextCell value={row.destination != null ? DESTINATION_LABEL[row.destination] : null} />,
  },
  {
    key: 'lineCount',
    header: 'Items',
    width: 90,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={String(row.lineCount)} mono numeric />,
  },
  {
    // THE LIFECYCLE, beside the row's own live-or-deleted pill — the shell places it before status.
    key: 'stage',
    header: 'Stage',
    width: 130,
    sortable: true,
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
